import { createFileRoute, Link } from '@tanstack/react-router'
import { cx } from '@tutlo/ui'
import { DEVICES, type DeviceId } from '../hero/preview.ts'
import { DEFAULT_HERO, HERO_TESTS, type HeroTest } from '../hero/tests.ts'
import { HERO_VERSIONS, type HeroVersion } from '../hero/versions.ts'

type GallerySearch = { urzadzenie?: DeviceId; wszystkie?: boolean }

/**
 * Every hero version side by side, live from the code (HMR while developing): `/podglad`.
 * Frames are `/podglad/<id>`, so nothing here creates leads, views or analytics events.
 */
export const Route = createFileRoute('/podglad/')({
  validateSearch: (raw: Record<string, unknown>): GallerySearch => ({
    ...(DEVICES.some((d) => d.id === raw.urzadzenie)
      ? { urzadzenie: raw.urzadzenie as DeviceId }
      : {}),
    ...(raw.wszystkie === true || raw.wszystkie === 'true' ? { wszystkie: true } : {}),
  }),
  component: Gallery,
})

const FIGMA = 'https://www.figma.com/design/8ORxH44yttxtlMwqfn6RBR/?node-id='
const versions: ReadonlyArray<HeroVersion> = HERO_VERSIONS

function roleIn(test: HeroTest, id: string): string | null {
  const i = test.arms.findIndex((a) => a.version === id)
  if (i < 0) return null
  return `${test.id}${i === 0 ? ' · kontrola' : ''}`
}

function Gallery() {
  const { urzadzenie = 'telefon', wszystkie = false } = Route.useSearch()
  const device = DEVICES.find((d) => d.id === urzadzenie) ?? DEVICES[0]
  const running = HERO_TESTS.find((t) => t.status === 'running')
  const current = new Set<string>([
    DEFAULT_HERO,
    ...HERO_TESTS.filter((t) => t.status !== 'ended').flatMap((t) => t.arms.map((a) => a.version)),
  ])
  const shown = [...versions].reverse().filter((v) => wszystkie || current.has(v.id) || !v.frozen)

  return (
    <main className="min-h-dvh bg-bg px-base py-lg desktop:px-2xl">
      <header className="mb-lg flex flex-col gap-md">
        <div className="flex flex-wrap items-baseline gap-md">
          <h1 className="text-h2">Wersje hero</h1>
          <span className="text-label text-text-secondary">
            podgląd bez zbierania danych · rejestr: apps/web/src/hero
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          {DEVICES.map((d) => (
            <Link
              key={d.id}
              to="/podglad"
              search={(s) => ({ ...s, urzadzenie: d.id })}
              className={cx(
                'rounded-pill border px-md py-xs text-label',
                d.id === device.id
                  ? 'border-selected bg-selected-soft text-selected'
                  : 'border-border-soft bg-bg-card',
              )}
            >
              {d.label} {d.width}×{d.height}
            </Link>
          ))}
          <Link
            to="/podglad"
            search={{ urzadzenie: device.id, ...(wszystkie ? {} : { wszystkie: true }) }}
            className="ml-sm text-label text-selected underline"
          >
            {wszystkie ? 'Tylko aktualne i szkice' : 'Pokaż też archiwum'}
          </Link>
        </div>
        <TestsSummary running={running} />
      </header>

      <div className="flex flex-wrap items-start gap-lg">
        {shown.map((v) => (
          <VersionCard key={v.id} version={v} device={device} />
        ))}
      </div>
    </main>
  )
}

function TestsSummary({ running }: { running: HeroTest | undefined }) {
  return (
    <ul className="flex flex-col gap-xs text-label">
      {[...HERO_TESTS].reverse().map((t) => (
        <li key={t.id}>
          <span className="text-label-strong">
            {t.id} · {t.name}
          </span>{' '}
          <span className="text-text-secondary">
            · {t.status === 'running' ? 'trwa' : t.status === 'ended' ? 'zakończony' : 'szkic'}
            {t.start ? ` od ${t.start}` : ''}
            {t.end ? ` do ${t.end}` : ''} ·{' '}
            {t.arms.map((a) => `${a.version} (${a.weight})`).join(' vs ')}
          </span>
        </li>
      ))}
      {!running && (
        <li className="text-text-secondary">Żaden test nie trwa: wszyscy widzą {DEFAULT_HERO}.</li>
      )}
    </ul>
  )
}

function Badge({
  children,
  tone,
}: {
  children: React.ReactNode
  tone: 'violet' | 'green' | 'grey'
}) {
  return (
    <span
      className={cx(
        'rounded-pill px-sm py-[2px] text-[11px] uppercase tracking-[0.06em]',
        tone === 'violet' && 'bg-selected-soft text-selected',
        tone === 'green' && 'bg-success-soft text-success-text',
        tone === 'grey' && 'bg-bg text-text-secondary',
      )}
    >
      {children}
    </span>
  )
}

function VersionCard({
  version,
  device,
}: {
  version: HeroVersion
  device: (typeof DEVICES)[number]
}) {
  const roles = HERO_TESTS.map((t) => ({ test: t, role: roleIn(t, version.id) })).filter(
    (r) => r.role,
  )
  const src = `/podglad/${version.id}`
  return (
    <article
      className="flex flex-col gap-sm rounded-card-lg border border-border-soft bg-bg-card p-md shadow-card"
      style={{ width: device.width * device.scale + 26 }}
    >
      <div className="flex flex-wrap items-center gap-xs">
        <span className="text-body-strong">
          {version.id} · {version.name}
        </span>
        {version.id === DEFAULT_HERO && <Badge tone="green">domyślna</Badge>}
        {roles.map(({ test, role }) => (
          <Badge key={test.id} tone={test.status === 'running' ? 'violet' : 'grey'}>
            {role}
          </Badge>
        ))}
        {!version.frozen && <Badge tone="grey">szkic</Badge>}
      </div>
      <div
        className="overflow-hidden rounded-card-sm border border-border-subtle"
        style={{ width: device.width * device.scale, height: device.height * device.scale }}
      >
        <iframe
          title={`${version.id} ${device.label}`}
          src={src}
          loading="lazy"
          style={{
            width: device.width,
            height: device.height,
            transform: `scale(${device.scale})`,
            transformOrigin: '0 0',
            border: 0,
          }}
        />
      </div>
      <p className="text-label text-text-secondary">{version.description}</p>
      <p className="text-[12px] text-text-secondary">
        {version.live?.length
          ? `Na żywo: ${version.live.map((l) => `${l.from} – ${l.to ?? 'dziś'}`).join(', ')}`
          : 'Jeszcze nie była domyślną wersją'}
        {' · '}
        <code>src/hero/versions/{version.file}</code>
      </p>
      <div className="flex flex-wrap gap-md text-label">
        <a href={src} target="_blank" rel="noreferrer" className="text-selected underline">
          Otwórz na pełnym ekranie
        </a>
        <a
          href={`${src}?stan=failed`}
          target="_blank"
          rel="noreferrer"
          className="text-selected underline"
        >
          Stan błędu
        </a>
        {version.figma && (
          <a
            href={FIGMA + version.figma.replace(':', '-')}
            target="_blank"
            rel="noreferrer"
            className="text-selected underline"
          >
            Figma
          </a>
        )}
      </div>
    </article>
  )
}
